import { signal } from '@preact/signals';
import { h, render } from 'preact';
import type { RequestType } from '@feedback/shared';
import { mountShadowRoot } from './mount';
import type { MountedShadowRoot } from './mount';
import { createApiClient } from './api/client';
import type { ApiClient, CreateApiClientOptions } from './api/client';
import { AuthError } from './api/types';
import { createMetadataStore } from './metadata/collector';
import type { IdentifyPayload, MetadataStore } from './metadata/collector';
import { createRingBuffer, installConsoleCapture } from './metadata/console-buffer';
import type { ConsoleErrorEntry } from './metadata/console-buffer';
import { App, DEFAULT_REQUEST_TYPE } from './ui/App';
import { SnapdomCapturer } from './screenshot/capturer';
import type { ScreenshotCapturer } from './screenshot/capturer';
import { submitFeedback } from './submit';
import { createCaptureUploads } from './attachments/capture-upload';
import { MediaStreamRecorder, isRecordingSupported } from './recording/recorder';
import { filedCloseDetail } from './status-link';
import type { FiledReport, ModalCapture, ModalSubmitPayload, ModalSubmitResult } from './ui/Modal';

export interface FeedbackTrackerInitOptions {
  /** Public integration key, sent as `X-Feedback-Key` on every request. */
  key: string;
  /** Backend origin, e.g. `https://api.feedbacktracker.app`. */
  apiBase?: string;
  /**
   * CSS selectors to mask before rasterizing a screenshot.
   *
   * Consumed by `SnapdomCapturer.capture`, which calls `maskElements` BEFORE
   * rasterizing, so masked content never exists in the captured bitmap. (This
   * comment said "consumed by a later task" long after that task landed, which
   * is how the capability came to look unbuilt from outside the package.)
   */
  redactSelectors?: string[];
  /**
   * Opt in to capturing recent `console.error`/`warn` output and attaching it to submitted
   * reports as `metadata.consoleErrors`. Off by default (privacy) — nothing is wrapped or
   * captured unless this is explicitly truthy.
   */
  captureConsole?: boolean;

  /**
   * @internal Test seam — lets tests inject a stub API client instead of hitting the
   * network. Not part of the public `FeedbackTracker` surface; underscore-prefixed and
   * intentionally undocumented for host apps.
   */
  _createApiClient?: (opts: CreateApiClientOptions) => ApiClient;
  /**
   * @internal Test seam — lets tests observe/intercept the shadow-root mount (e.g. to grab
   * the closed `shadowRoot` reference, which is otherwise unreachable from outside). Not
   * part of the public `FeedbackTracker` surface.
   */
  _mountShadowRoot?: (hostEl?: HTMLElement) => MountedShadowRoot;
  /**
   * @internal Test seam — lets tests inject a fake `ScreenshotCapturer` instead of the real
   * `SnapdomCapturer` (which needs a real browser; see `./screenshot/capturer.ts`'s doc
   * comment for why jsdom can't run it). Not part of the public `FeedbackTracker` surface.
   */
  _createCapturer?: () => ScreenshotCapturer;
}

export interface WidgetController {
  init(opts: FeedbackTrackerInitOptions): void;
  identify(user: IdentifyPayload): void;
  setContext(ctx: Record<string, unknown>): void;
  open(type?: RequestType): void;
  close(): void;
  /** Hides the tab and the drawer without discarding the draft. For host modals and kiosks. */
  hide(): void;
  /** Undoes `hide()`. */
  show(): void;
  /**
   * Docks the trigger into a host element and hides the edge tab.
   * Phones otherwise keep the edge tab (option A). Option B is this call.
   */
  mountTrigger(el: HTMLElement): void;
  destroy(): void;
}

function safeWarn(message: string): void {
  try {
    console.warn(`[FeedbackTracker] ${message}`);
  } catch {
    // Even console access could theoretically throw in a locked-down host — the widget
    // must never let anything escape into the host page, not even a logging call.
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Creates a fresh widget controller instance. `src/index.ts` calls this once at module
 * scope to back the `FeedbackTracker` global (the "singleton" from the host page's point of
 * view); tests call it repeatedly to get isolated instances.
 *
 * Holds the api client, metadata store, and shadow-root mount handle for one widget
 * instance, plus open/close state as a signal. Every exported method (and the async
 * continuation inside `init`) is a boundary: wrapped in try/catch so a failure anywhere
 * logs at most one `console.warn` and never throws into the host app. Methods are plain
 * closures (not object-literal methods relying on `this`) so they remain safe to export
 * standalone, e.g. `export const open = controller.open` — a bare call with no receiver.
 */
export function createController(): WidgetController {
  let api: ApiClient | undefined;
  let metadata: MetadataStore | undefined;
  let mounted: MountedShadowRoot | undefined;
  // Set only when `captureConsole` was truthy for the current `init()` — calling it restores
  // the wrapped `console.error`/`warn` to their originals. Left undefined (never called) when
  // console capture wasn't opted into, honoring the privacy-off-by-default requirement.
  let uninstallConsoleCapture: (() => void) | undefined;
  // Bumped on every teardown so a still-in-flight `getConfig()` continuation from a
  // superseded `init()` call (re-init, or destroy() while loading) can recognize it's stale
  // and no-op instead of rendering into / tearing down a mount point it no longer owns.
  let generation = 0;

  const isOpen = signal(false);
  // Which `RequestType` the modal should pre-select next time it opens — set by `open(type)`,
  // read by `App`/`Modal` via `initialType`. Defaults to the same default `open()` falls back
  // to when called with no argument.
  const requestedType = signal<RequestType>(DEFAULT_REQUEST_TYPE);
  const triggerHidden = signal(false);
  const dockHost = signal<HTMLElement | null>(null);
  // Set by the drawer's success path, consumed by the next `fm:close`.
  let filedReport: FiledReport | undefined;

  function publish(name: 'fm:open' | 'fm:close', detail?: { reference?: string; id?: string; href?: string }): void {
    if (typeof document === 'undefined') return;
    try {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
    } catch {
      // A host that froze CustomEvent must not hear about it as an exception.
    }
  }

  function teardown(): void {
    generation += 1;
    if (mounted) {
      try {
        // Unmount the Preact tree first so every component's effect cleanups run (e.g.
        // `Modal`'s `document` keydown listener, any live object URLs) — `mounted.unmount()`
        // below only removes the host element from the DOM, which does NOT trigger Preact's
        // cleanup lifecycle.
        render(null, mounted.container);
      } catch {
        // ignore — unmount is best-effort during teardown
      }
      try {
        mounted.unmount();
      } catch {
        // ignore — unmount is best-effort during teardown
      }
    }
    mounted = undefined;
    api = undefined;
    metadata = undefined;
    if (uninstallConsoleCapture) {
      try {
        uninstallConsoleCapture();
      } catch {
        // ignore — restoring console is best-effort during teardown
      }
      uninstallConsoleCapture = undefined;
    }
    isOpen.value = false;
    triggerHidden.value = false;
    dockHost.value = null;
    filedReport = undefined;
  }

  function open(type?: RequestType): void {
    try {
      requestedType.value = type ?? DEFAULT_REQUEST_TYPE;
      const wasOpen = isOpen.value;
      isOpen.value = true;
      if (!wasOpen) publish('fm:open');
    } catch (err) {
      safeWarn(`open() failed: ${errorMessage(err)}`);
    }
  }

  function close(): void {
    try {
      const wasOpen = isOpen.value;
      isOpen.value = false;
      if (wasOpen) {
        const detail = filedCloseDetail(filedReport);
        filedReport = undefined;
        publish('fm:close', detail);
      }
    } catch (err) {
      safeWarn(`close() failed: ${errorMessage(err)}`);
    }
  }

  function hide(): void {
    try {
      triggerHidden.value = true;
    } catch (err) {
      safeWarn(`hide() failed: ${errorMessage(err)}`);
    }
  }

  function show(): void {
    try {
      triggerHidden.value = false;
    } catch (err) {
      safeWarn(`show() failed: ${errorMessage(err)}`);
    }
  }

  function mountTrigger(el: HTMLElement): void {
    try {
      if (!el || typeof el.appendChild !== 'function') return;
      dockHost.value = el;
    } catch (err) {
      safeWarn(`mountTrigger() failed: ${errorMessage(err)}`);
    }
  }

  function noteFiled(filed: FiledReport): void {
    filedReport = filed;
  }

  function destroy(): void {
    try {
      teardown();
    } catch (err) {
      safeWarn(`destroy() failed: ${errorMessage(err)}`);
    }
  }

  function identify(user: IdentifyPayload): void {
    try {
      metadata?.identify(user);
    } catch (err) {
      safeWarn(`identify() failed: ${errorMessage(err)}`);
    }
  }

  function setContext(ctx: Record<string, unknown>): void {
    try {
      metadata?.setContext(ctx);
    } catch (err) {
      safeWarn(`setContext() failed: ${errorMessage(err)}`);
    }
  }

  function init(opts: FeedbackTrackerInitOptions): void {
    try {
      if (!opts || !opts.key) {
        safeWarn('init() requires a "key" option — widget not initialized.');
        return;
      }
      if (!opts.apiBase) {
        safeWarn('init() requires an "apiBase" option — widget not initialized.');
        return;
      }
      // Captured as a local so it stays narrowed to `string` inside the `.then()` closure
      // below — TS's control-flow narrowing on `opts.apiBase` doesn't cross that boundary.
      const apiBase = opts.apiBase;

      // Re-initializing (e.g. a host app calling init() twice) tears down the previous
      // instance first rather than leaking a second mount point.
      if (mounted) {
        teardown();
      }

      const clientFactory = opts._createApiClient ?? createApiClient;
      const mountFn = opts._mountShadowRoot ?? mountShadowRoot;
      // Defaults to a real `SnapdomCapturer` here (not inside `Modal`/`App`, which just
      // receive it as a prop) — the one place production wiring decides what implements
      // `ScreenshotCapturer`; `_createCapturer` lets tests substitute a fake.
      const capturerFactory = opts._createCapturer ?? (() => new SnapdomCapturer());

      api = clientFactory({ apiBase, key: opts.key });
      // One per init, bound to this init's client: the leading capture starts
      // uploading when it lands and Send picks the upload up (I24294, ADR 0045).
      const captureUploads = createCaptureUploads(api);

      // Console capture is opt-in and off by default (privacy): only install the
      // console.error/warn wrapper — and feed its ring buffer into the metadata store — when
      // the host explicitly asked for it via `captureConsole`/`data-feedback-capture-console`.
      if (opts.captureConsole) {
        const consoleBuffer = createRingBuffer<ConsoleErrorEntry>();
        uninstallConsoleCapture = installConsoleCapture(consoleBuffer);
        metadata = createMetadataStore({ getConsoleErrors: () => consoleBuffer.toArray() });
      } else {
        metadata = createMetadataStore();
      }

      mounted = mountFn();
      const capturer = capturerFactory();

      const container = mounted.container;
      const myGeneration = generation;

      api
        .getConfig()
        .then((config) => {
          if (myGeneration !== generation) return; // superseded by a later init()/destroy()
          try {
            // Config resolved successfully — mount the real, appearance-aware `App`/`Button`
            // (Task 5) driven by the just-fetched `WidgetConfig`.
            //
            // Uses h(App, ...) (not a direct App(...) call) so Preact creates a proper
            // component vnode — required for @preact/signals' auto-tracking to subscribe
            // this render to `isOpen` and re-render when open()/close() flip it.
            //
            // Real submit flow: uploads attachments + POSTs via `submitFeedback`, closing over
            // this init's `api`/`metadata` (not a snapshot — re-reads the enclosing `let`s at
            // call time). `Modal` drives the in-flight/success/error UI around the returned
            // promise; a rejection here becomes its error/retry state, never an uncaught throw.
            // Guarded against a `destroy()` that raced ahead of the user clicking Submit — at
            // that point `api`/`metadata` are cleared, so this rejects instead of throwing.
            const handleModalSubmit = (
              payload: ModalSubmitPayload,
              attachments: File[],
              capture: ModalCapture | null,
            ): Promise<ModalSubmitResult> => {
              if (!api || !metadata) {
                return Promise.reject(new Error('FeedbackTracker is no longer active'));
              }
              return submitFeedback({
                api,
                metadata,
                form: payload,
                attachments,
                capture,
                uploadCapture: captureUploads.get,
              });
            };

            render(
              h(App, {
                isOpen,
                requestedType,
                onOpen: open,
                onClose: close,
                onFiled: noteFiled,
                triggerHidden,
                dockHost,
                onSubmit: handleModalSubmit,
                onCaptureReady: captureUploads.start,
                config,
                capturer,
                redactSelectors: opts.redactSelectors,
                // Constructed only where it can work. `getDisplayMedia` is absent
                // in every non-secure context and in several mobile browsers, and
                // `Modal` hides the control when this is undefined rather than
                // offering a button whose only outcome is an error.
                recorder: isRecordingSupported() ? new MediaStreamRecorder() : undefined,
                apiBase,
              }),
              container,
            );
          } catch (renderErr) {
            safeWarn(`failed to render widget: ${errorMessage(renderErr)}`);
            teardown();
          }
        })
        .catch((err: unknown) => {
          if (myGeneration !== generation) return; // superseded by a later init()/destroy()
          // Fail silent: no button, exactly one warning, never throw. An AuthError covers
          // three indistinguishable causes on this surface — a bad key, a disallowed
          // origin, or a paused/inactive application (IntegrationKeyGuard's lifecycle
          // gates) — all surfaced as the same 403/401, so the message names all three
          // rather than steering an admin toward rotating keys or editing the allowlist
          // when the real cause is a portal toggle. Any other failure (network, 5xx,
          // etc.) is treated the same way — the widget must not surface API problems to
          // the host. Wrapped in try/catch (like the .then branch) so a throw here can't
          // become an unhandled promise rejection — this is the async tail of the
          // never-throw boundary.
          try {
            if (err instanceof AuthError) {
              safeWarn(
                'failed to initialize — invalid integration key, origin not allowed, or collection paused.',
              );
            } else {
              safeWarn(`failed to load widget configuration: ${errorMessage(err)}`);
            }
            teardown();
          } catch {
            // never propagate to the host page
          }
        });
    } catch (err) {
      safeWarn(`init() failed: ${errorMessage(err)}`);
    }
  }

  return { init, identify, setContext, open, close, hide, show, mountTrigger, destroy };
}
